import { useRef, useState } from 'react'
import { Button, SearchCombobox } from '../common'
import { resolveComponentVariant } from '../common/componentContract'
import { normalizeSearchQuery, useSearchQuery } from '../../hooks/useSearchQuery'
import { legSearchApi } from '../../services/api'
import { parseOutcomeId } from '../../utils/odds'
import BorrowedSearchNote from './BorrowedSearchNote'
import LegRow from './LegRow'

const ALL_ZEROES_PATTERN = /^0+$/
const UNSIGNED_INTEGER_PATTERN = /^\d+$/

// What each product searches and shows. Prepacks and Affiliate links build
// only from games that have not started; a Feed post can attach live legs.
// Links open on a decimal-odds sportsbook, so they show decimal prices.
const CONTEXTS = {
  feed: { eventStatus: '', oddsFormat: 'american', priceNote: 'unverified', coverage: true },
  prepacks: { eventStatus: 'upcoming', oddsFormat: 'american', priceNote: 'catalog price' },
  'affiliate-links': { eventStatus: 'upcoming', oddsFormat: 'decimal', priceNote: 'catalog price' },
}
const EMPTY_OFFERINGS = []
const EMPTY_IDS = []

const resultKey = (result) => result?.leg?.outcome_id

/**
 * Search legs on GET /legs/search. `offerings` are the regulations to search
 * (none: the organization's default); `onSelect` gets the search result, whose
 * `leg` is a Prepacks leg. Selection rules stay with the host.
 * `onAddOutcomeId` lets a host that needs only the ID (the Feed composer)
 * attach an outcome ID that search failed or found nothing for: its own
 * validation checks it on Kambi without Search.
 * A host that searches outside one organization (Command Center) passes its
 * own `search(request, { signal })`, answering in the same shape, and a
 * `scope` that keys the cache in place of `orgSlug`. `coverage` overrides the
 * context's "x/y regulations" note, for a host whose search does not cover
 * every one of `offerings`. `controls` render above the search box, inside
 * the combobox.
 */
export default function LegSearch({
  context,
  orgSlug,
  scope,
  search: searchLegs,
  offerings = EMPTY_OFFERINGS,
  query,
  onQueryChange,
  selectedIds = EMPTY_IDS,
  onSelect,
  onAddOutcomeId,
  role,
  regulationMappings,
  enabled = true,
  coverage,
  controls,
  oddsFormat,
  label = 'Search markets',
  help = 'Search starts after two characters. Displayed prices are rechecked before publish.',
  placeholder = 'Search players, teams, events, leagues, markets, or a market/outcome ID',
  emptyMessage = 'No markets matched. Try another search.',
}) {
  // An unknown context falls back to the narrowest search: upcoming games only.
  const settings = CONTEXTS[resolveComponentVariant('LegSearch context', context, Object.keys(CONTEXTS), 'prepacks')]
  const format = oddsFormat || settings.oddsFormat
  const showCoverage = coverage ?? settings.coverage
  const offeringKey = offerings.join('\u0000')
  // An all-digit query looks the number up as an outcome ID, then as a market
  // ID; neither has ID 0.
  const zeroId = ALL_ZEROES_PATTERN.test(normalizeSearchQuery(query))
  const searchScope = scope || orgSlug
  // Names this box's searches, in the order it sends them, so its next one
  // cancels the one still running on the server: aborting the request alone
  // never reaches it.
  const [searchSession] = useState(() => crypto.randomUUID())
  const searchSeq = useRef(0)
  const search = useSearchQuery({
    queryKey: ['leg-search', context, searchScope, offeringKey, settings.eventStatus],
    query,
    search: ({ query: q, cursor, signal }) => {
      searchSeq.current += 1
      const request = { q, offeringIds: offerings, eventStatus: settings.eventStatus, cursor, searchSession, searchSeq: searchSeq.current }
      return searchLegs ? searchLegs(request, { signal }) : legSearchApi.search(orgSlug, request, { signal })
    },
    getItemKey: resultKey,
    enabled: enabled && Boolean(searchScope) && !zeroId,
  })
  const selected = new Set(selectedIds.map(String))
  const typedOutcomeId = onAddOutcomeId ? parseOutcomeId(search.query) : null
  const sources = search.pages.flatMap((page) => page.sources || [])

  const notes = (result) => [
    settings.priceNote,
    showCoverage
      ? (offerings.length ? `${result.available_in?.length || 0}/${offerings.length} regulations` : 'Configured regulations')
      : null,
  ].filter(Boolean)

  return (
    <SearchCombobox
      label={label}
      help={help}
      placeholder={placeholder}
      noun="market"
      disabled={!enabled}
      query={query}
      onQueryChange={onQueryChange}
      items={search.results}
      getItemKey={resultKey}
      renderItem={(result) => (
        <LegRow
          leg={result.leg}
          oddsFormat={format}
          status={settings.eventStatus ? undefined : result.event_status}
          notes={notes(result)}
        />
      )}
      isSelected={(result) => selected.has(String(result.leg?.outcome_id))}
      onSelect={onSelect}
      status={search.status}
      error={search.error?.message}
      warnings={search.warnings}
      // An all-digit query is an outcome or market ID lookup on the organization's own books.
      emptyMessage={UNSIGNED_INTEGER_PATTERN.test(search.query) ? `No open market or outcome has ID ${search.query} on the searched books.` : emptyMessage}
      hasMore={search.hasMore}
      loadingMore={search.isLoadingMore}
      onLoadMore={search.loadMore}
      notes={<BorrowedSearchNote sources={sources} role={role} mappings={regulationMappings} />}
      controls={controls}
      fallback={typedOutcomeId && !selected.has(String(typedOutcomeId)) ? (
        <Button type="button" variant="secondary" size="sm" className="justify-self-start" onClick={() => onAddOutcomeId(typedOutcomeId)}>
          Add outcome {typedOutcomeId} by ID
        </Button>
      ) : null}
    />
  )
}
